export function calculateUserPoints(userData) {
  const carryForwardPoints = Number(userData.carryForwardPoints || 0);
  const pacesetterCreated = Number(userData.pacesetterCreated || 0);
  const baseCp = Number(userData.baseCp || 0);
  const accumulatedCp = Number(userData.accumulatedCp || 0);

  const pacesetterPoints = pacesetterCreated * 5;
  const totalCpGrowth = accumulatedCp - baseCp;

  const cpGrowthPoints = Math.min(
    Math.floor(Math.max(totalCpGrowth, 0) / 4000) * 20,
    40
  );

  const confirmedPoints =
    carryForwardPoints + pacesetterPoints + cpGrowthPoints;

  const startDate = new Date("2026-04-18");
  const endDate = new Date("2026-09-26");
  const today = new Date();

  const totalWeeks = (endDate - startDate) / (1000 * 60 * 60 * 24 * 7);
  const weeksPassed = Math.max(
    1,
    Math.floor((today - startDate) / (1000 * 60 * 60 * 24 * 7))
  );

  const remainingWeeks = Math.max(1, totalWeeks - weeksPassed);

  const weeklyAvg4000 = Math.max(
    0,
    (baseCp + 4000 - accumulatedCp) / remainingWeeks
  );

  const weeklyAvg8000 = Math.max(
    0,
    (baseCp + 8000 - accumulatedCp) / remainingWeeks
  );

  return {
    carryForwardPoints,
    pacesetterCreated,
    pacesetterPoints,
    baseCp,
    accumulatedCp,
    totalCpGrowth,
    cpGrowthPoints,
    confirmedPoints,
    weeklyAvg4000: Number(weeklyAvg4000.toFixed(1)),
    weeklyAvg8000: Number(weeklyAvg8000.toFixed(1)),
  };
}

// ── Helper: parse YYYY-MM-DD string in LOCAL time (not UTC) ──
// new Date("2026-06-21") parses as UTC midnight, which can shift the
// day in calculations. This forces local-time interpretation.
function parseLocalDate(value) {
  if (!value) return new Date(NaN);
  const str = String(value).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return new Date(str);
}

export function getDirectorStatus(director) {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // normalize to start of today (local time)

  const comQuaExpiryDate = parseLocalDate(director.comQuaExpiryDate);
  const fiftyCpExpiryDate = parseLocalDate(director.fiftyCpExpiryDate);

  const comQuaDaysLeft = Math.ceil(
    (comQuaExpiryDate - today) / (1000 * 60 * 60 * 24)
  );

  const fiftyCpDaysLeft = Math.ceil(
    (fiftyCpExpiryDate - today) / (1000 * 60 * 60 * 24)
  );

  const isValid = comQuaDaysLeft >= 0 && fiftyCpDaysLeft >= 0;

  const nearestExpiryDays = Math.min(comQuaDaysLeft, fiftyCpDaysLeft);

  let warningLevel = "normal";

  if (nearestExpiryDays <= 3) {
    warningLevel = "critical";
  } else if (nearestExpiryDays <= 7) {
    warningLevel = "warning";
  }

  return {
    isValid,
    comQuaDaysLeft,
    fiftyCpDaysLeft,
    nearestExpiryDays,
    warningLevel,
  };
}

export function calculateDirectorPoints(directors) {
  // Only "qualified" directors contribute confirmed points (10 pts each).
  // Max cap: 10 points total per sponsor.
  const qualifiedCount = directors.filter(
    (d) => d.qualificationStatus === "qualified"
  ).length;
  return Math.min(qualifiedCount * 10, 10);
}

// ── Reserved director points ──
// The 10 pts sit in "Reserved" while a director is uploaded but not yet
// marked qualified. Once marked qualified, points graduate to Actual and
// the Reserved bucket drops to 0.
export function calculateDirectorReservedPoints(directors) {
  const hasQualified = directors.some(
    (d) => d.qualificationStatus === "qualified"
  );
  if (hasQualified) return 0; // graduated — nothing left to reserve
  return directors.length > 0 ? 10 : 0;
}